import { useCallback, useEffect } from 'react';
import { Language, StatusOutput } from '@/api/types';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useGetStatus } from '@/api/services';
import * as Sentry from '@sentry/react';

import { useSessionStorage, SessionStorageKeys } from '@/utils/storage';
import { DEFAULT_LANGUAGE, useAppLanguage } from '@/hooks/useAppLanguage.ts';

const resolveLocalizedData = (language: Language, data: StatusOutput | undefined) => {
  if (!data) {
    return { introVideoSrc: '', questionsTotal: 0 };
  }

  return {
    introVideoSrc: data[`${language}_intro`] ?? data.english_intro,
    questionsTotal: data[`questions_total_${language}`] ?? data.questions_total_english,
  };
}

export const useQuestionsAnswers = () => {
  const location = useLocation();
  const [language, setLanguage] = useAppLanguage();
  const [currentQuestionNumber, setCurrentQuestionNumber] =
    useSessionStorage<number>(SessionStorageKeys.CURRENT_QUESTION_NUMBER, 0);
  const [invitationKey, _setInvitationKey] = useSessionStorage<string>(
    SessionStorageKeys.INVITATION_KEY
  );

  const [params] = useSearchParams();
  const paramsInvitationKey = params.get(SessionStorageKeys.INVITATION_KEY);

  const setInvitationKey = useCallback(
    (invitationKey) => {
      _setInvitationKey(invitationKey);
      setCurrentQuestionNumber(0);
    },
    [_setInvitationKey, setCurrentQuestionNumber]
  );

  const { data, isError, error, isFetching, isLoading } =
    useGetStatus(invitationKey, language);

  useEffect(() => {
    if (paramsInvitationKey && paramsInvitationKey !== invitationKey) {
      setInvitationKey(paramsInvitationKey);
      if (location.pathname === '/') {
        setLanguage(DEFAULT_LANGUAGE);
      }
    }
  }, [invitationKey, paramsInvitationKey, setInvitationKey]);

  useEffect(() => {
    if (invitationKey) {
      Sentry.setUser({ id: invitationKey });
    }
  }, [invitationKey]);

  const { questionsTotal, introVideoSrc } = resolveLocalizedData(language, data);

  return {
    invitationKey,
    remoteStatus: data,
    localeData: {
      introVideoSrc,
      questionsTotal,
      estimatedTime: questionsTotal * 2,
    },
    language,
    currentQuestionNumber: currentQuestionNumber,
    langContext: {
      language,
      intro: introVideoSrc,
      questionsTotal: questionsTotal,
    },
    isLoading: isLoading && isFetching, // isFetching is used considering that we are using conditional fetch
    isError,
    error,
    setLanguage,
    setCurrentQuestionNumber,
    setInvitationKey,
  };
};
